import { PageTitle } from '../../../../_metronic/layout/core'
import { Button } from '../../../components/Button/Button'
import { Input } from '../../../components/Input/Input'
import { Loading } from '../../../components/Loading/Loading'
import React, { FC, useEffect, useState } from 'react'
import type { Pages } from './types'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { BreadCrumbs } from '../../../components/BreadCrumbs/breadCrumbrs'
import { useFeaturesPluginsSpace } from './factory/useFeaturesPluginspace'
import type { CreateFeatureDto } from './types'
import { GET } from '../../../services/api'

enum SegmentType {
    fashion = 'fashion',
    general = 'general',
    food = 'food',
    varejo = 'varejo',
    hotel = 'hospitality',
}

type FeatureFormErrors = Partial<Record<'name' | 'description' | 'segmentType', string>>

const segmentOptions = [
    { select: 'Moda', value: SegmentType.fashion },
    { select: 'Geral', value: SegmentType.general },
    { select: 'Alimentacao', value: SegmentType.food },
    { select: 'Varejo', value: SegmentType.varejo },
    { select: 'Hospitalidade', value: SegmentType.hotel },
]

const normalizeFeatureCode = (value: string) =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .trim()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/_{2,}/g, '_')

const generateAutomaticCode = (name: string) => {
    const normalizedName = normalizeFeatureCode(name)

    if (normalizedName) {
        return normalizedName
    }

    return `FEATURE_${Date.now()}`
}

const FeaturesPluginspaceContent: FC = () => {
    const navigate = useNavigate()
    const { createFeature } = useFeaturesPluginsSpace()
    const [loading, setLoading] = useState(false)
    const [feature, setFeature] = useState<CreateFeatureDto>({
        name: '',
        description: '',
        code: '',
        segmentType: SegmentType.fashion,
    })
    const [errors, setErrors] = useState<FeatureFormErrors>({})
    const [pages, setPages] = useState<Pages[]>([])
    const [codeTouched, setCodeTouched] = useState(false)

    const validateForm = (payload: CreateFeatureDto) => {
        const nextErrors: FeatureFormErrors = {}

        if (!payload.name.trim()) {
            nextErrors.name = 'Informe o nome da feature'
        }

        if (!payload.description.trim()) {
            nextErrors.description = 'Informe a descricao da feature'
        }

        if (!payload.segmentType) {
            nextErrors.segmentType = 'Selecione o segmento da feature'
        }

        setErrors(nextErrors)

        return Object.keys(nextErrors).length === 0
    }

    const updateField = <K extends keyof CreateFeatureDto>(field: K, value: CreateFeatureDto[K]) => {
        setFeature((previous) => ({ ...previous, [field]: value }))
        setErrors((previous) => ({ ...previous, [field]: undefined }))
    }

    const handleCreate = async () => {
        const generatedCode = feature.code.trim() || generateAutomaticCode(feature.name)
        const payload: CreateFeatureDto = {
            ...feature,
            name: feature.name.trim(),
            description: feature.description.trim(),
            code: normalizeFeatureCode(generatedCode),
        }

        if (!validateForm(payload)) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos obrigatorios',
                text: 'Revise os campos destacados antes de salvar.',
            })
            return
        }

        try {
            setLoading(true)
            const createdFeature = await createFeature(payload)

            if (!createdFeature?.success) {
                Swal.fire({
                    icon: createdFeature?.typemsg || 'error',
                    title: 'Erro ao criar feature',
                    text: createdFeature?.message || 'Nao foi possivel criar a feature. Tente novamente.',
                })
                return
            }

            setFeature(payload)

            await Swal.fire({
                icon: 'success',
                title: 'Feature criada',
                text: 'A feature foi criada com sucesso.',
            })

            navigate('/features_pluginspaces')
        } finally {
            setLoading(false)
        }
    }

    async function getPages() {
        try {
            setLoading(true)
            const pagesRes = await GET('/pages')
            setPages(pagesRes.data || [])
            setLoading(false)

        } catch (e) {
            setLoading(false)
            console.log(e)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao buscar paginas!',
            })
        }
    }

    useEffect(() => {
        getPages()
    }, [])

    useEffect(() => {
        if (codeTouched) {
            return
        }

        const automaticCode = feature.name.trim() ? generateAutomaticCode(feature.name) : ''

        setFeature((previous) => {
            if (previous.code === automaticCode) {
                return previous
            }

            return {
                ...previous,
                code: automaticCode,
            }
        })
    }, [codeTouched, feature.name])

    if (loading) {
        return <Loading />
    }

    return (
        <div className='row g-5 gx-xxl-12'>
            <div className='card card-xxl-stretch mb-5 mb-xl-12'>
                <div className='card-header py-5'>
                    <h3 className='card-title align-items-start flex-column'>
                        <span className='card-label fw-bolder fs-3 mb-1'>
                            Criar Feature Pluginspace
                        </span>
                        <BreadCrumbs />
                        <span className='text-muted mt-1 fw-bold fs-7'>
                            Preencha as informacoes abaixo para cadastrar uma nova feature.
                        </span>
                    </h3>
                </div>

                <div className='card-body border-bottom'>
                    <div className='row'>
                        <div className='col-md-6'>
                            <Input
                                inputAttr={{ type: 'default' }}
                                label='Nome'
                                placeholder='Digite o nome da feature'
                                value={feature.name}
                                change={(value: string) => updateField('name', value)}
                                alarm={!!errors.name}
                                alarmText={errors.name}
                                required
                            />
                        </div>

                        <div className='col-md-6'>
                            <Input
                                inputAttr={{ type: 'select' }}
                                label='Segmento'
                                value={feature.segmentType}
                                change={(value: SegmentType) => updateField('segmentType', value)}
                                options={segmentOptions}
                                alarm={!!errors.segmentType}
                                alarmText={errors.segmentType}
                                required
                            />
                        </div>

                        <div className='col-md-6'>
                            <Input
                                inputAttr={{ type: 'default' }}
                                label='Codigo'
                                placeholder='Gerado automaticamente se ficar vazio'
                                value={feature.code}
                                change={(value: string) => {
                                    setCodeTouched(true)
                                    updateField('code', normalizeFeatureCode(value))
                                }}
                                blur={(value: string) => {
                                    const normalizedCode = normalizeFeatureCode(value)

                                    setFeature((previous) => ({
                                        ...previous,
                                        code: normalizedCode,
                                    }))

                                    if (!normalizedCode) {
                                        setCodeTouched(false)
                                    }
                                }}
                                smalltext='Se nao preencher, o codigo sera gerado automaticamente com base no nome.'
                            />
                        </div>

                        <div className='col-md-6'>
                            <Input
                                inputAttr={{ type: 'select' }}
                                label='Pagina'
                                search
                                placeholder='Selecione a pagina'
                                value={feature.pageId ? { label: pages.find((page) => page.id === feature.pageId)?.name || '', value: feature.pageId.toString() } : null}
                                change={(value: { label: string; value: string }) => {
                                    updateField('pageId', Number(value.value))
                                }}
                               options={pages.filter(e => e.menu === 1 && e.type === feature.segmentType).map((page) => ({ select: page.name, value: page.id.toString() }))}
                               
                            />
                        </div>

                        <div className='col-12'>
                            <Input
                                inputAttr={{ type: 'textarea' }}
                                label='Descricao'
                                placeholder='Descreva a finalidade da feature'
                                value={feature.description}
                                change={(value: string) => updateField('description', value)}
                                rows={4}
                                maxCaracteres={255}
                                alarm={!!errors.description}
                                alarmText={errors.description}
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className='d-flex w-100'>
                    <div className='col-6 d-flex justify-content-start'>
                        <Button
                            text='Voltar'
                            color='primary'
                            size='small'
                            horizontalPadding={6}
                            btnAttr={{
                                type: 'button',
                            }}
                            click={() => navigate('/features_pluginspaces')}
                            btnClass='fw-bolder m-9'
                        />
                    </div>

                    <div className='col-6 d-flex justify-content-end'>
                        <Button
                            text='Salvar'
                            color='primary'
                            size='small'
                            horizontalPadding={6}
                            btnAttr={{
                                type: 'button',
                            }}
                            click={() => handleCreate()}
                            loading={loading}
                            btnClass='fw-bolder m-9'
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

const FeaturesPluginspaceCreate: FC = () => {
    return (
        <>
            <PageTitle
                breadcrumbs={[
                    { title: 'Features Pluginspace', path: 'features_pluginspaces', isActive: true },
                    { title: 'Criar', path: 'features_pluginspaces/create', isActive: true },
                ]}
            >
                Criar Feature Pluginspace
            </PageTitle>
            <FeaturesPluginspaceContent />
        </>
    )
}

export { FeaturesPluginspaceCreate }